/**
 * Converts technical error messages to user-friendly messages
 */
export function refineErrorMessage(error: any): string {
  // Extract error message from various error formats
  const rawMessage =
    error?.response?.data?.message ||
    error?.message ||
    String(error);

  // Network errors
  if (rawMessage.includes("Network Error") || rawMessage.includes("fetch failed")) {
    return "Unable to connect to the server. Please check your internet connection and try again.";
  }

  if (rawMessage.includes("timeout")) {
    return "The request took too long. Please try again.";
  }

  // Authentication errors
  if (rawMessage.includes("401") || rawMessage.includes("Unauthorized")) {
    return "Your session has expired. Please log in again.";
  }

  if (rawMessage.includes("403") || rawMessage.includes("Forbidden")) {
    return "You don't have permission to perform this action.";
  }

  if (rawMessage.includes("expired token") || rawMessage.includes("Invalid token")) {
    return "Your session has expired. Please log in again.";
  }

  // Wallet/Privy errors
  if (rawMessage.includes("Wallet address not found") || rawMessage.includes("embedded wallet")) {
    return "We couldn't access your wallet. Please try logging out and logging back in.";
  }

  if (rawMessage.includes("User not found") || rawMessage.includes("sign up first")) {
    return "Account not found. Please sign up or try logging in again.";
  }

  // Server errors
  if (rawMessage.includes("500") || rawMessage.includes("Internal server error")) {
    return "Something went wrong on our end. Please try again later.";
  }

  if (rawMessage.includes("503") || rawMessage.includes("Service Unavailable")) {
    return "The service is temporarily unavailable. Please try again in a few moments.";
  }

  // Database/Connection errors
  if (rawMessage.includes("database") || rawMessage.includes("connection")) {
    return "We're having trouble connecting to our servers. Please try again.";
  }

  // Challenge-specific errors
  if (rawMessage.includes("already joined")) {
    return "You've already joined this challenge.";
  }

  if (rawMessage.includes("Challenge not found")) {
    return "This challenge doesn't exist or has been removed.";
  }

  if (rawMessage.includes("ended") || rawMessage.includes("already ended")) {
    return "This challenge has already ended.";
  }

  if (rawMessage.includes("Insufficient")) {
    return "You don't have enough funds to complete this action.";
  }

  // Transaction errors
  if (rawMessage.includes("rejected") || rawMessage.includes("denied") || rawMessage.includes("cancelled")) {
    return "Transaction was cancelled. No charges were made.";
  }

  if (rawMessage.includes("gas")) {
    return "Transaction failed due to network fees. Please try again.";
  }

  // Strava errors
  if (rawMessage.includes("Strava")) {
    return "Unable to connect to Strava. Please try reconnecting your account.";
  }

  // Validation errors - these are usually user-friendly already
  if (
    rawMessage.includes("required") ||
    rawMessage.includes("invalid") ||
    rawMessage.includes("must be") ||
    rawMessage.includes("cannot be")
  ) {
    // Return validation errors as-is, they're usually clear
    return rawMessage;
  }

  // Generic fallback
  return "Something went wrong. Please try again.";
}

/**
 * Get a user-friendly title for an error
 */
export function getErrorTitle(error: any): string {
  const rawMessage =
    error?.response?.data?.message ||
    error?.message ||
    String(error);

  if (rawMessage.includes("Network") || rawMessage.includes("timeout")) {
    return "Connection Error";
  }

  if (rawMessage.includes("401") || rawMessage.includes("403") || rawMessage.includes("Unauthorized")) {
    return "Authentication Error";
  }

  if (rawMessage.includes("Wallet") || rawMessage.includes("Transaction")) {
    return "Transaction Error";
  }

  if (rawMessage.includes("Challenge")) {
    return "Challenge Error";
  }

  if (rawMessage.includes("Strava")) {
    return "Strava Connection Error";
  }

  return "Error";
}
